import { Notification } from "@bbb/model";
import { Button } from "@primitive/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@primitive/dialog";
import { ScrollArea } from "@primitive/scroll-area";
import {
  getNotificationCategory,
  getNotificationCategoryLabel,
  getNotificationTimeAgo,
  sortNotificationsByNewest,
} from "@lib/notificationPresentation";

type NotificationDetailsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: Notification[];
  title: string;
  onNotificationClick: (notification: Notification) => void;
};

function getDetailRows(notification: Notification) {
  const metadata = notification.metadata;

  return [
    ["Category", getNotificationCategoryLabel(getNotificationCategory(notification.type))],
    ["Community", metadata?.communityName],
    ["Campaign", metadata?.campaignName],
    ["Quest", metadata?.questTitle],
    ["Status", metadata?.status],
    ["Referral", metadata?.referralName],
    ["Reward", metadata?.rewardType],
    ["Prize", metadata?.prizeAmount],
    ["Blocks earned", metadata?.blocksEarned?.toString()],
  ].filter((row): row is [string, string] => Boolean(row[1]));
}

export function NotificationDetailsDialog({
  open,
  onOpenChange,
  notifications,
  title,
  onNotificationClick,
}: NotificationDetailsDialogProps) {
  const sortedNotifications = sortNotificationsByNewest(notifications);
  const hasMultipleNotifications = sortedNotifications.length > 1;

  const handleOpenNotification = (notification: Notification) => {
    onOpenChange(false);
    onNotificationClick(notification);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(92vw,560px)]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {hasMultipleNotifications
              ? "Review grouped notification details."
              : "Review the full notification details."}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="space-y-3">
            {sortedNotifications.map((notification) => {
              const detailRows = getDetailRows(notification);

              return (
                <div
                  key={notification.id}
                  className="rounded-md border bg-card p-3 text-card-foreground"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium">
                        {notification.title}
                      </h3>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {getNotificationTimeAgo(notification.timestamp)}
                      </p>
                    </div>
                    {!notification.read && (
                      <span className="mt-1.5 size-2 shrink-0 rounded-full bg-blue-500" />
                    )}
                  </div>

                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                    {notification.message}
                  </p>

                  {detailRows.length > 0 && (
                    <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                      {detailRows.map(([label, value]) => (
                        <div key={label} className="min-w-0">
                          <dt className="text-muted-foreground">{label}</dt>
                          <dd className="truncate font-medium text-foreground">
                            {value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  <div className="mt-3 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenNotification(notification)}
                    >
                      Open
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
